/**
 * sections/testimonials.liquid
 *
 * <expert-slider> — one green card whose text layers ([data-content]) crossfade, and a deck of
 *   video cards ([data-card]) re-dealt on every switch: data-pos 0 = front, 1 = right ghost,
 *   2 = left ghost. CSS transitions turn the re-deal into a card shuffle. Cards leaving the
 *   front pause their video.
 * [data-play] inside [data-video] — swaps the poster for the <video> kept in a <template>.
 */
/** Deck slots, mirrored from the CSS (`.tm-video[data-pos]`). */
const SLOT = {
  front: { x: 0, y: 0, r: 0, s: 1 },
  right: { x: 9, y: 0, r: 6, s: 0.94 },
  left: { x: -9, y: 0, r: -6, s: 0.94 },
};
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/** @param {{x:number,y:number,r:number,s:number}} p */
const transformOf = (p) => `translate(${p.x}%, ${p.y}%) rotate(${p.r}deg) scale(${p.s})`;

class ExpertSlider extends HTMLElement {
  #index = 0;
  #token = 0;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.#deal();
    this.#showText(this.#index);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  get cards() {
    return Array.from(this.querySelectorAll('[data-card]'));
  }

  /**
   * @param {number} index
   * @param {1 | -1} [direction] 1 = next (front card swings out to the right), -1 = previous
   */
  select(index, direction = 1) {
    const count = this.cards.length;
    if (!count) return;
    const next = ((index % count) + count) % count;
    if (next === this.#index) return;
    const previous = this.#index;
    this.#index = next;

    if (count < 2 || this.#reducedMotion.matches) {
      this.#deal();
      this.#showText(next);
      return;
    }
    this.#shuffle(previous, next, direction);
  }

  /** Slot of card `i` for the active index: 0 front, 1 right ghost, 2 left ghost, 3+ tucked. */
  #posOf(i, active = this.#index) {
    const count = this.cards.length;
    return (((i - active) % count) + count) % count;
  }

  /** Static state: data-pos on every card (CSS places them), counter in sync. */
  #deal() {
    this.cards.forEach((card) => {
      const pos = this.#posOf(Number(card.getAttribute('data-card')));
      card.setAttribute('data-pos', String(pos));
      card.classList.remove('is-moving');
      card.style.zIndex = '';
      if (pos !== 0) card.querySelectorAll('video').forEach((video) => video.pause());
    });
    this.querySelectorAll('[data-counter-current]').forEach((el) => {
      el.textContent = String(this.#index + 1);
    });
  }

  /**
   * The card shuffle, as one continuous sequence (Web Animations API):
   *   0 – 380ms  front card lifts and swings out past the deck's edge, on top of everything
   *   120 – 820ms the next card glides forward into the front slot; the other ghost moves up a slot
   *   380 – 900ms the old front card dives behind the deck into the last ghost slot
   * @param {number} from
   * @param {number} to
   * @param {1 | -1} direction
   */
  async #shuffle(from, to, direction) {
    const token = ++this.#token;
    const cards = this.cards;
    const count = cards.length;

    // Settle anything still flying from a previous click before starting over
    cards.forEach((card) => card.getAnimations().forEach((animation) => animation.cancel()));

    const outgoing = cards.find((c) => Number(c.getAttribute('data-card')) === from);
    const incoming = cards.find((c) => Number(c.getAttribute('data-card')) === to);
    if (!outgoing || !incoming) return this.#deal();

    outgoing.querySelectorAll('video').forEach((video) => video.pause());
    this.#fadeText(to, token);
    this.querySelectorAll('[data-counter-current]').forEach((el) => {
      el.textContent = String(to + 1);
    });

    const slotOf = (pos) => (pos === 0 ? SLOT.front : pos === 1 ? SLOT.right : SLOT.left);
    const swingOut = direction > 0 ? { x: 34, y: 2, r: 16, s: 0.96 } : { x: -36, y: 2, r: -14, s: 0.96 };
    const outgoingTarget = slotOf(this.#posOf(from, to));
    const running = [];

    // Every other card glides to its new slot (incoming comes to the front)
    cards.forEach((card) => {
      if (card === outgoing) return;
      const i = Number(card.getAttribute('data-card'));
      const fromSlot = slotOf(this.#posOf(i, from));
      const toSlot = slotOf(this.#posOf(i, to));
      card.classList.add('is-moving');
      card.style.zIndex = card === incoming ? '3' : '2';
      card.setAttribute('data-pos', String(this.#posOf(i, to))); // poster/veil fade via CSS
      running.push(
        card.animate(
          [{ transform: transformOf(fromSlot) }, { transform: transformOf(toSlot) }],
          { duration: 700, delay: 120, easing: EASE, fill: 'both' }
        ).finished
      );
    });

    // Outgoing: swing out on top…
    outgoing.classList.add('is-moving');
    outgoing.style.zIndex = '4';
    const swing = outgoing.animate(
      [{ transform: transformOf(SLOT.front) }, { transform: transformOf(swingOut) }],
      { duration: 380, easing: 'cubic-bezier(0.4, 0, 0.6, 1)', fill: 'both' }
    );
    await this.#settled(swing.finished, 600);
    if (token !== this.#token) return;

    // …then dive behind the deck into the last slot
    outgoing.style.zIndex = '0';
    outgoing.setAttribute('data-pos', String(this.#posOf(from, to)));
    running.push(
      outgoing.animate(
        [{ transform: transformOf(swingOut) }, { transform: transformOf(outgoingTarget) }],
        { duration: 520, easing: EASE, fill: 'both' }
      ).finished
    );

    await this.#settled(Promise.allSettled(running), 1400);
    if (token !== this.#token) return;
    cards.forEach((card) => card.getAnimations().forEach((animation) => animation.cancel()));
    this.#deal();
  }

  /**
   * Resolves when `promise` settles or after `ms` — a background tab freezes animations and
   * their `finished` promises, so the deck must never be left mid-flight.
   * @param {Promise<unknown>} promise
   * @param {number} ms
   */
  #settled(promise, ms) {
    return Promise.race([promise.catch(() => {}), new Promise((resolve) => setTimeout(resolve, ms))]);
  }

  /** @param {number} index */
  #showText(index) {
    this.querySelectorAll('[data-content]').forEach((content) => {
      content.hidden = Number(content.getAttribute('data-content')) !== index;
    });
  }

  /** Text layers: fade out (220ms), swap, fade in — in step with the cards. */
  async #fadeText(index, token) {
    const layers = this.querySelector('.tm-expert__layers');
    if (!layers) return this.#showText(index);
    layers.classList.add('is-fading');
    await new Promise((resolve) => setTimeout(resolve, 220));
    if (token !== this.#token) return;
    this.#showText(index);
    layers.classList.remove('is-fading');
  }

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-prev]')) this.select(this.#index - 1, -1);
    else if (target.closest('[data-next]')) this.select(this.#index + 1, 1);
    else if (target.closest('[data-play]')) this.#play(target.closest('[data-video]'));
  };

  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft') this.select(this.#index - 1, -1);
    if (event.key === 'ArrowRight') this.select(this.#index + 1, 1);
  };

  /** @param {HTMLElement | null} card */
  #play(card) {
    if (!card || card.getAttribute('data-pos') !== '0') return;
    const template = card.querySelector('template[data-video-template]');
    if (!template) return;

    let video = card.querySelector('video');
    if (!video) {
      // importNode, not template.content.cloneNode: iOS Safari never starts loading a <video>
      // created in the template's inert document (black player, 00:00), even after insertion.
      card.append(document.importNode(template.content, true));
      video = card.querySelector('video');
      video?.load();
    }
    card.classList.add('is-playing');
    video?.play().catch(() => {});
  }
}

if (!customElements.get('expert-slider')) customElements.define('expert-slider', ExpertSlider);
